import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  InputBase, 
  IconButton, 
  Paper, 
  Popper,
  Box,
  Typography,
  ClickAwayListener,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@emotion/styled';

const SearchResult = styled('div')`
  padding: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  
  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.isRecent && `
    background-color: #f8f8f8;
  `}
`;

const SearchResultContent = styled('div')`
  flex: 1;
`;

const StatusIndicator = styled('div')`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => {
    if (props.isRecent) return '#4caf50';
    return props.status === 'Canlı' ? '#4caf50' : '#999';
  }};
  margin-top: 6px;
  
  ${props => props.isRecent && `
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  `}
`;

const SearchSystem = ({ reports }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
  
    

    // Arama işlemi
    const results = reports.filter(report => 
      report.name.toLowerCase().includes(value.toLowerCase()) ||
      (typeof report.brand === 'object' 
        ? report.brand.name.toLowerCase().includes(value.toLowerCase())
        : report.brand.toLowerCase().includes(value.toLowerCase())) ||
      report.status.toLowerCase().includes(value.toLowerCase())
    );

    // Sonuçları son değişiklik tarihine göre sırala
    const sortedResults = results.sort((a, b) => {
      // Son 24 saat içinde değiştirilmiş raporları üste taşı
      const isRecentA = a.lastModified && (new Date() - new Date(a.lastModified)) < 24 * 60 * 60 * 1000;
      const isRecentB = b.lastModified && (new Date() - new Date(b.lastModified)) < 24 * 60 * 60 * 1000;
      
      if (isRecentA && !isRecentB) return -1;
      if (!isRecentA && isRecentB) return 1;
      
      // Aynı durumdaki raporları tarihe göre sırala
      return new Date(b.lastModified || 0) - new Date(a.lastModified || 0);
    });

    // Gerçek bir API çağrısını simüle etmek için setTimeout kullanıyoruz
    setTimeout(() => {
      setSearchResults(sortedResults);
      setIsSearching(false);
    }, 300);
  };

  const handleClickAway = () => {
    setAnchorEl(null);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleResultClick = (report) => {
    setAnchorEl(null);
    setSearchResults([]);
    setSearchTerm('');

    if (report?.id) {
      navigate(`/dashboard/reports/${report.id}`);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <IconButton 
          onClick={handleIconClick}
          sx={{ 
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <SearchIcon />
        </IconButton>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          style={{
            zIndex: 1400,
            marginTop: '4px',
            width: '300px'
          }}
        >
          <Paper
            sx={{
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
            }}
          >
            <Paper
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                boxShadow: 'none',
                borderRadius: '8px 8px 0 0',
                '&:hover': {
                  border: '1px solid #bdbdbd',
                },
              }}
            >
              <InputBase
                autoFocus
                sx={{ ml: 1, flex: 1 }}
                placeholder="Raporlarda ara..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <IconButton sx={{ p: '10px', color: '#666' }}>
                <SearchIcon />
              </IconButton>
            </Paper>

            {(isSearching || searchResults.length > 0 || searchTerm.trim() !== '') && (
              <Paper
                sx={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  borderTop: '1px solid #eee',
                  borderRadius: '0 0 8px 8px'
                }}
              >
                {isSearching ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : searchResults.length > 0 ? (
                  searchResults.map(report => {
                    const isRecent = report.lastModified && 
                      (new Date() - new Date(report.lastModified)) < 24 * 60 * 60 * 1000;
                    
                    return (
                      <SearchResult 
                        key={report.id} 
                        onClick={() => handleResultClick(report)}
                        isRecent={isRecent}
                      >
                        <StatusIndicator 
                          status={report.status} 
                          isRecent={isRecent}
                        />
                        <SearchResultContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#240B4B' }}>
                            {report.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                            {typeof report.brand === 'object' ? report.brand.name : report.brand}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: report.status === 'Canlı' ? '#4caf50' : '#999',
                                fontWeight: 500
                              }}
                            >
                              {report.status}
                            </Typography>
                            {isRecent && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#4caf50',
                                  fontWeight: 500,
                                  fontSize: '0.7rem'
                                }}
                              >
                                • Son 24 saat içinde güncellendi
                              </Typography>
                            )}
                          </Box>
                        </SearchResultContent>
                      </SearchResult>
                    );
                  })
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
                    <Typography variant="body2">
                      Sonuç bulunamadı
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchSystem; 